import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { RequestStatus } from '@/enums/request'
import { API_BASE_URL } from '@/libs/apiBase'
import { chime, notifyBrowser } from '@/libs/chime'
import i18n from '@/libs/i18n'
import { defaultRequestSettings, isOpen, kindLabel, requestSummary } from '@/libs/request'
import { useAppStore } from '@/stores/app'
import { useFeedbackStore } from '@/stores/feedback'
import { useFetchStore } from '@/stores/fetch'
import type { IFeedback } from '@/types/Feedback'
import type { IRequest, IRequestSettings } from '@/types/Request'

/*
 * THE GUEST REQUESTS of the tenant being worked in ("Zahteve gostov", docs/API.md §13.5), live.
 * One list for the whole console, so the inbox and the menu's badge agree: GET …/requests once,
 * then the console's event stream (GET …/requests/events — an EventSource, authenticated by the
 * JWT cookie, as it cannot send a header) keeps it current. The stream has no backlog, so every
 * (re)connect reads the list again. The API already keeps a kitchen user to room service.
 *
 * A NEW pending request rings (a short tone from the Web Audio API — no sound file) and, where
 * the person allowed it, shows a browser notification: reception is not always looking at the
 * screen (`libs/chime.ts`). So does a guest's LATE CANCEL of an accepted order (§13.1.1) — the
 * kitchen must stop.
 *
 * The house's usual preparation time (RequestSettings `prep_minutes`) is read beside the list:
 * the inbox preselects it as the estimate when the kitchen accepts an order "čim prej".
 *
 * GUEST FEEDBACK rides the same stream (`event: feedback`, §13.5 / §18.4) for the roles that may
 * see it: the stream hands it to `stores/feedback.ts`, and every (re)connect makes that store read
 * its list again too.
 */

/** How many days of closed requests the inbox shows beside the open ones. */
export const REQUEST_DAYS = 2

/** How long a stream the server refused (an expired session, a lost membership) waits to retry. */
const RETRY_MS = 5_000

/** A browser notification of a new request (or of a late cancel), where the person allowed them. */
const notify = (request: IRequest) => {
    const what = request.late_cancel ? i18n.global.t('requests.late_cancel') : kindLabel(request.kind)

    notifyBrowser(`#${request.number} ${what} — ${request.room_name}`, requestSummary(request), `request-${request.id}-${request.status}`)
}

export const useRequestsStore = defineStore('requests', () => {
    const appStore      = useAppStore()
    const fetchStore    = useFetchStore()
    const feedbackStore = useFeedbackStore()

    const tid       = ref('')
    const items     = ref<IRequest[]>([])
    const loaded    = ref(false)
    const connected = ref(false)
    const sound     = ref(true)
    /** RequestSettings `room_service.prep_minutes` of the tenant followed (the API's default until read). */
    const prepMinutes = ref(defaultRequestSettings().room_service.prep_minutes)

    let stream: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | null = null

    const pendingCount = computed((): number => items.value.filter((item) => item.status === RequestStatus.Pending).length)

    /** Open first (oldest first), then the rest (newest first) — the API's own order. */
    const sorted = computed((): IRequest[] => [...items.value].sort((a, b) => {
        const openA = isOpen(a)
        const openB = isOpen(b)

        if (openA !== openB) return openA ? -1 : 1

        return openA ? a.created_at.localeCompare(b.created_at) : b.created_at.localeCompare(a.created_at)
    }))

    const load = async () => {
        const forTid = tid.value
        if (!forTid) return

        const { statusCode, data } = await fetchStore.fetch(`/tenants/${forTid}/requests?days=${REQUEST_DAYS}`).get().json<IRequest[]>()

        if (tid.value !== forTid || statusCode.value !== 200) return

        items.value  = data.value ?? []
        loaded.value = true
    }

    /** The usual preparation time; an older tv-api without it keeps the default. */
    const loadSettings = async () => {
        const forTid = tid.value
        if (!forTid) return

        const { statusCode, data } = await fetchStore.fetch(`/tenants/${forTid}/requests/settings`).get().json<IRequestSettings>()

        if (tid.value !== forTid || statusCode.value !== 200) return

        prepMinutes.value = data.value?.room_service.prep_minutes || defaultRequestSettings().room_service.prep_minutes
    }

    /** A request as it is now: replaced in place, or added (and announced when it is new and pending, or just cancelled late). */
    const upsert = (request: IRequest) => {
        const index = items.value.findIndex((item) => item.id === request.id)

        if (index >= 0) {
            const lateNow = Boolean(request.late_cancel) && !items.value[index].late_cancel

            items.value.splice(index, 1, request)

            if (lateNow) {
                if (sound.value) chime()
                notify(request)
            }

            return
        }

        items.value = [request, ...items.value]

        if (request.status === RequestStatus.Pending) {
            if (sound.value) chime()
            notify(request)
        }
    }

    const close = () => {
        if (retry) clearTimeout(retry)
        retry = null
        stream?.close()
        stream          = null
        connected.value = false
    }

    const open = () => {
        close()

        if (!tid.value || typeof EventSource === 'undefined') return

        const opened = new EventSource(`${API_BASE_URL}/tenants/${tid.value}/requests/events`)

        opened.addEventListener('open', () => {
            connected.value = true
            // The stream has no backlog: whatever happened while it was down is in the list.
            void load()
            void loadSettings()
            void feedbackStore.load()
        })

        // A network blip is retried by the EventSource itself; a refusal (non-200) closes it for
        // good, so the store opens a new one a little later while it still follows the tenant.
        opened.addEventListener('error', () => {
            connected.value = false

            if (opened.readyState !== EventSource.CLOSED || stream !== opened) return

            retry = setTimeout(() => { if (stream === opened && tid.value) open() }, RETRY_MS)
        })

        opened.addEventListener('request', (event) => {
            try {
                upsert(JSON.parse((event as MessageEvent<string>).data) as IRequest)
            } catch {
                // A line that is not a request: the next list read sets it right.
            }
        })

        opened.addEventListener('feedback', (event) => {
            try {
                feedbackStore.upsert(JSON.parse((event as MessageEvent<string>).data) as IFeedback, true)
            } catch {
                // A line that is not feedback: the next list read sets it right.
            }
        })

        stream = opened
    }

    /** Follows a tenant (the menu calls it with the tenant being worked in); '' stops. */
    const follow = (forTid: string) => {
        const allowed = !!forTid && appStore.canHandleRequests(forTid)
        const next    = allowed ? forTid : ''

        if (next === tid.value && (stream || !next)) return

        tid.value    = next
        items.value  = []
        loaded.value = false

        feedbackStore.follow(next && appStore.handlesFeedback(next) ? next : '')

        if (!next) {
            close()

            return
        }

        open()
    }

    /** Asks the browser for notifications — from a click, as browsers require. */
    const askNotifications = async (): Promise<string> => {
        if (typeof Notification === 'undefined') return 'denied'

        return Notification.requestPermission()
    }

    const notificationsAllowed = (): boolean => typeof Notification !== 'undefined' && Notification.permission === 'granted'

    const notificationsSupported = (): boolean => typeof Notification !== 'undefined'

    // Signing out ends the stream (the menu that follows the tenant may be gone already).
    watch(() => appStore.JWT, (jwt) => {
        if (jwt) return

        close()
        tid.value   = ''
        items.value = []
        feedbackStore.follow('')
    })

    /** A request changed by this console: in the list at once, before the stream says so. */
    const replace = (request: IRequest) => upsert(request)


    return {
        tid, items, loaded, connected, sound, prepMinutes, pendingCount, sorted,
        follow, load, loadSettings, replace, close, askNotifications, notificationsAllowed, notificationsSupported,
    }
})
